
import { supabase } from '@/integrations/supabase/client';
import { SlackIntegration } from '../../types';
import { dbLogger } from '../utils/dbLogger';

/**
 * Fetches Slack integration settings for a specific AI staff
 */
export const fetchSlackIntegration = async (aiStaffId: string): Promise<SlackIntegration | null> => {
  try {
    dbLogger.info("Fetching Slack integration", { aiStaffId });
    
    // Ensure aiStaffId is properly formatted for query
    if (!aiStaffId) {
      dbLogger.error("Invalid aiStaffId provided to fetchSlackIntegration", 
        new Error("Missing aiStaffId"), { aiStaffId });
      return null;
    }
    
    // Use maybeSingle instead of single to prevent errors when no data is found
    const { data, error } = await supabase
      .from('slack_integrations')
      .select('*')
      .eq('ai_staff_id', aiStaffId)
      .maybeSingle();
    
    if (error) {
      dbLogger.error("Error fetching Slack integration", error, { aiStaffId });
      // Return null instead of throwing, to handle the error gracefully
      return null;
    }
    
    // If no data is found, return null without error
    if (!data) {
      dbLogger.info("No Slack integration found for staff", { aiStaffId });
      return null;
    }
    
    // Transform the data to ensure it matches the SlackIntegration interface
    const slackIntegration: SlackIntegration = {
      id: data.id,
      ai_staff_id: data.ai_staff_id,
      webhook_url: data.webhook_url,
      slack_token: data.slack_token,
      trigger_tag: data.trigger_tag,
      is_file_processing_enabled: data.is_file_processing_enabled,
      is_auto_responses_enabled: data.is_auto_responses_enabled,
      is_enabled: true // Set a default value since this property is not in the database
    };
    
    if (data.created_at) slackIntegration.created_at = data.created_at;
    if (data.updated_at) slackIntegration.updated_at = data.updated_at;
    
    return slackIntegration;
  } catch (error) {
    dbLogger.error("Exception in fetchSlackIntegration", error, { aiStaffId });
    // Return null instead of re-throwing, to handle the error gracefully
    return null;
  }
};
